import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { DEFAULT_PAGE_SIZE, useOrderAndPaginationControl } from '../../../table/hooks';
import { useUpdateSearchParams } from '../../../../hooks/useUpdateSearchParams';
import { readPageSize, readTableOrder } from '../../../table/tablePreferencesStorage';
import { OVERVIEW_STATUS_FILTER, OVERVIEW_TAB } from '../constants';
import type { OverviewTab } from '../types';

const OVERVIEW_TAB_PARAM = 'tab';
const OVERVIEW_STATUS_PARAM = 'status';
const OVERVIEW_SEARCH_PARAM = 'search';
export const BY_PROJECT_TABLE_SORT = {
    tableId: 'measuring-points.by-project',
    sortableFields: ['name', 'city', 'total'],
} as const;
export const STANDALONE_TABLE_SORT = {
    tableId: 'measuring-points.standalone',
    sortableFields: ['name', 'city'],
} as const;

function getTableSortOptions(tab: OverviewTab) {
    return tab === OVERVIEW_TAB.STANDALONE ? STANDALONE_TABLE_SORT : BY_PROJECT_TABLE_SORT;
}

function getOverviewTab(searchParams: URLSearchParams): OverviewTab {
    const tab = searchParams.get(OVERVIEW_TAB_PARAM);

    switch (tab) {
        case OVERVIEW_TAB.BY_PROJECT:
        case OVERVIEW_TAB.STANDALONE:
            return tab;
        default:
            return OVERVIEW_TAB.BY_PROJECT;
    }
}

function getStatusFilter(searchParams: URLSearchParams): boolean {
    return searchParams.get(OVERVIEW_STATUS_PARAM) === OVERVIEW_STATUS_FILTER.ARCHIVED;
}

/** Whether the overview shows archived instead of active items. */
export function useIsArchivedTab(): boolean {
    const { searchParams } = useUpdateSearchParams();
    return getStatusFilter(searchParams);
}

export function useOverviewTabState() {
    const { searchParams, updateSearchParams, updateSearchParamsBatch } = useUpdateSearchParams();
    const selectedTab = getOverviewTab(searchParams);
    const isArchivedTab = getStatusFilter(searchParams);
    const isStandaloneTab = selectedTab === OVERVIEW_TAB.STANDALONE;

    const initialSearchTerm = searchParams.get(OVERVIEW_SEARCH_PARAM) ?? '';
    const [searchTerm, setSearchTermState] = useState(initialSearchTerm);
    const debouncedSearchTerm = useDebouncedValue(searchTerm);
    const previousDebouncedSearchRef = useRef(debouncedSearchTerm);
    const orderAndPaginationControl = useOrderAndPaginationControl(
        getTableSortOptions(selectedTab)
    );
    const { currentPage, pageCount, setCurrentPage } = orderAndPaginationControl;

    const setSearchTerm = useCallback(
        (nextSearchTerm: string) => {
            setSearchTermState(nextSearchTerm);
        },
        [setSearchTermState]
    );

    const setSelectedTab = useCallback(
        (nextView: OverviewTab) => {
            if (nextView === selectedTab) {
                return;
            }
            const targetTableSort = getTableSortOptions(nextView);
            const targetOrderBy = readTableOrder(
                targetTableSort.tableId,
                targetTableSort.sortableFields
            );

            updateSearchParamsBatch({
                [OVERVIEW_TAB_PARAM]:
                    nextView === OVERVIEW_TAB.STANDALONE ? OVERVIEW_TAB.STANDALONE : '',
                page: 1,
                pageSize: readPageSize(targetTableSort.tableId) ?? DEFAULT_PAGE_SIZE,
                orderBy: targetOrderBy ?? '',
            });
        },
        [selectedTab, updateSearchParamsBatch]
    );

    const setArchiveFilter = useCallback(
        (nextArchived: boolean) => {
            if (isArchivedTab === nextArchived) {
                return;
            }

            if (currentPage !== 1) {
                setCurrentPage(1);
            }

            updateSearchParams(
                OVERVIEW_STATUS_PARAM,
                nextArchived ? OVERVIEW_STATUS_FILTER.ARCHIVED : OVERVIEW_STATUS_FILTER.ACTIVE
            );
        },
        [currentPage, isArchivedTab, setCurrentPage, updateSearchParams]
    );

    useEffect(() => {
        const nextSearchTerm = searchParams.get(OVERVIEW_SEARCH_PARAM) ?? '';
        setSearchTermState((currentSearchTerm) =>
            currentSearchTerm === nextSearchTerm ? currentSearchTerm : nextSearchTerm
        );
    }, [searchParams]);

    useEffect(() => {
        if (previousDebouncedSearchRef.current === debouncedSearchTerm) {
            return;
        }

        previousDebouncedSearchRef.current = debouncedSearchTerm;
        updateSearchParams(OVERVIEW_SEARCH_PARAM, debouncedSearchTerm.trim());

        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [debouncedSearchTerm, currentPage, setCurrentPage, updateSearchParams]);

    useEffect(() => {
        if (pageCount > 0 && currentPage > pageCount) {
            setCurrentPage(pageCount);
        }
    }, [currentPage, pageCount, setCurrentPage]);

    return {
        selectedTab,
        isArchivedTab,
        isStandaloneTab,
        setSelectedTab,
        setArchiveFilter,
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        orderAndPaginationControl,
    };
}
